import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogClose,
} from '@/components/ui/dialog';
import { meetingsApi, type MeetingSlot } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';

const TZ = 'Europe/Rome';
const dayLabel = (iso: string) => {
  const s = new Intl.DateTimeFormat('it-IT', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'long' }).format(new Date(iso));
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const timeLabel = (iso: string) =>
  new Intl.DateTimeFormat('it-IT', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

export interface InsertedMeeting { token: string; url: string; blockHtml: string; count: number; title: string }

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  accountId?: string;
  defaultTitle: string;
  onInsert: (m: InsertedMeeting) => void;
}

/**
 * "Proponi orari": suggerisce slot liberi dal calendario, l'utente seleziona
 * quelli da proporre e inserisce nella mail una card con i bottoni orario.
 * Il destinatario conferma da una pagina pubblica (nessun account richiesto).
 */
export function MeetingProposalDialog({ open, onOpenChange, accountId, defaultTitle, onInsert }: Props) {
  const [title, setTitle] = useState(defaultTitle || 'Chiamata');
  const [duration, setDuration] = useState(30);
  const [slots, setSlots] = useState<MeetingSlot[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [withVideo, setWithVideo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inserting, setInserting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(defaultTitle || 'Chiamata');
      void loadSuggestions(duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cambi rapidi 30m→60m: vale solo l'ULTIMA richiesta (le risposte stale vengono ignorate)
  const reqSeq = useRef(0);
  async function loadSuggestions(dur: number) {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const { slots: s } = await meetingsApi.suggest({ durationMinutes: dur, days: 7, count: 6 });
      if (seq !== reqSeq.current) return; // risposta vecchia: scarta
      setSlots(s);
      setSelected(new Set(s.map((x) => x.start))); // preselezionati tutti
    } catch (e) {
      if (seq === reqSeq.current) toast.error(e instanceof ApiError ? e.message : 'Suggerimento orari fallito');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }

  const byDay = useMemo(() => {
    const m = new Map<string, MeetingSlot[]>();
    for (const s of slots) {
      const d = dayLabel(s.start);
      if (!m.has(d)) m.set(d, []);
      m.get(d)!.push(s);
    }
    return [...m.entries()];
  }, [slots]);

  function toggle(s: MeetingSlot) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(s.start)) next.delete(s.start); else next.add(s.start);
      return next;
    });
  }

  async function handleInsert() {
    const chosen = slots.filter((s) => selected.has(s.start));
    if (chosen.length === 0) { toast.error('Seleziona almeno un orario'); return; }
    if (!title.trim()) { toast.error('Dai un titolo all’appuntamento'); return; }
    setInserting(true);
    try {
      const res = await meetingsApi.create({ accountId, title: title.trim(), durationMinutes: duration, slots: chosen, withVideo });
      onInsert({ token: res.token, url: res.url, blockHtml: res.blockHtml, count: chosen.length, title: title.trim() });
      onOpenChange(false);
      toast.success('Proposta orari inserita nella mail');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Creazione proposta fallita');
    } finally {
      setInserting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-primary" /> Proponi orari
          </DialogTitle>
          <DialogDescription>
            Orari liberi presi dal tuo calendario. Chi riceve la mail tocca un orario e
            l'appuntamento finisce dritto in calendario, con conferma per entrambi.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="mp-title">Titolo</Label>
              <Input id="mp-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Call conoscitiva" />
            </div>
            <div className="space-y-1.5">
              <Label>Durata</Label>
              <div className="flex gap-1">
                {[30, 60].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setDuration(d); void loadSuggestions(d); }}
                    className={cn(
                      'flex-1 h-9 rounded-md border text-sm font-medium transition-colors',
                      duration === d ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-accent'
                    )}
                  >
                    {d}m
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border p-3 min-h-[140px]">
            {loading ? (
              <div className="flex items-center justify-center h-28 text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Cerco gli orari liberi…
              </div>
            ) : byDay.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                Nessuno slot libero trovato nei prossimi 7 giorni.
              </div>
            ) : (
              <div className="space-y-3">
                {byDay.map(([day, items]) => (
                  <div key={day}>
                    <div className="text-xs font-semibold text-muted-foreground mb-1.5">{day}</div>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((s) => (
                        <button
                          key={s.start}
                          type="button"
                          onClick={() => toggle(s)}
                          className={cn(
                            'px-3 py-1.5 rounded-full border text-sm font-medium transition-colors',
                            selected.has(s.start)
                              ? 'bg-primary/10 border-primary text-primary'
                              : 'border-border text-muted-foreground hover:bg-accent'
                          )}
                        >
                          {timeLabel(s.start)}–{timeLabel(s.end)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center justify-between rounded-lg border p-3 cursor-pointer">
            <span className="text-sm">
              <span className="font-medium">Videochiamata</span>
              <span className="block text-xs text-muted-foreground">Alla prenotazione crea la stanza e manda il link nella conferma</span>
            </span>
            <input type="checkbox" checked={withVideo} onChange={(e) => setWithVideo(e.target.checked)} className="h-4 w-4" />
          </label>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{selected.size} orari selezionati · Fuso: Roma</span>
            <button type="button" onClick={() => void loadSuggestions(duration)} className="inline-flex items-center gap-1 hover:text-foreground">
              <RefreshCw className="h-3 w-3" /> Rigenera
            </button>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Annulla</Button>
          </DialogClose>
          <Button onClick={() => void handleInsert()} disabled={inserting || loading || selected.size === 0}>
            {inserting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Inserisci nella mail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
